import { applyAuthHeaders } from '@/api/authHeaders'
import { request } from '@/api/client'
import { env } from '@/config/env'

/** GET /developer/profile 的 data */
export interface DeveloperProfileDto {
  developer_name?: string
  website_url?: string
  developer_no?: string
  /** 自定义头像存储引用；空串表示未上传，经 /developer/files/* 取回 */
  avatar_url?: string
  /** IM 账号头像，仅在未上传自定义头像时回落 */
  im_avatar_url?: string
}

export interface DeveloperProfile {
  name: string
  website: string
  developerNo: string
  avatarUrl: string
  imAvatarUrl: string
}

export function mapDeveloperProfile(
  dto: DeveloperProfileDto | null | undefined,
): DeveloperProfile {
  return {
    name: dto?.developer_name?.trim() ?? '',
    website: dto?.website_url?.trim() ?? '',
    developerNo: dto?.developer_no?.trim() ?? '',
    avatarUrl: dto?.avatar_url?.trim() ?? '',
    imAvatarUrl: dto?.im_avatar_url?.trim() ?? '',
  }
}

/** 把存储引用换成可请求的 /developer/files/* 地址；已是 http(s) 则原样返回。 */
export function developerFileSrc(ref: string): string {
  const value = ref.trim()
  if (!value) return ''
  if (/^https?:\/\//i.test(value) || value.startsWith('data:') || value.startsWith('blob:')) {
    return value
  }
  const path = value.startsWith('/developer/files/')
    ? value
    : `/developer/files/${value.replace(/^\/+/, '')}`
  return `${env.apiBaseUrl}${path}`
}

/** PATCH /developer/profile。avatar 不传表示不修改。 */
export interface UpdateDeveloperProfileBody {
  developer_name: string
  website_url: string
  avatar?: string
}

/** data URL 取 base64 正文；已经是纯 base64 时原样返回。 */
export function toAvatarBase64(value: string): string {
  const trimmed = value.trim()
  const marker = 'base64,'
  const index = trimmed.indexOf(marker)
  return index >= 0 ? trimmed.slice(index + marker.length) : trimmed
}

/** GET /developer/profile */
export function getDeveloperProfile() {
  return request<DeveloperProfileDto>('/developer/profile').then(mapDeveloperProfile)
}

/** PATCH /developer/profile */
export function updateDeveloperProfile(input: {
  name: string
  website: string
  /** 新头像；不传则不修改。空串清除自定义头像。 */
  avatar?: string
}) {
  const body = {
    developer_name: input.name,
    website_url: input.website,
    ...(input.avatar !== undefined ? { avatar: toAvatarBase64(input.avatar) } : {}),
  } satisfies UpdateDeveloperProfileBody
  return request<DeveloperProfileDto | null>('/developer/profile', {
    method: 'PATCH',
    body,
  }).then((dto) => dto ?? {})
}

/** 自定义头像走鉴权取文件；没有自定义头像时用 IM 头像。 */
export async function loadDeveloperAvatar(profile: DeveloperProfile): Promise<string> {
  if (!profile.avatarUrl) return profile.imAvatarUrl
  if (/^https?:\/\//i.test(profile.avatarUrl) || profile.avatarUrl.startsWith('data:')) {
    return profile.avatarUrl
  }

  const headers = new Headers()
  applyAuthHeaders(headers)
  const res = await fetch(developerFileSrc(profile.avatarUrl), { headers })
  if (!res.ok) return profile.imAvatarUrl
  const type = res.headers.get('content-type') ?? ''
  if (type.includes('json')) return profile.imAvatarUrl
  return URL.createObjectURL(await res.blob())
}
